<template>
  <div :class="['app-card', { 
    'app-card--hover': hover,
    'app-card--flat': flat,
    'app-card--border': border,
    [`app-card--accent-${accent}`]: accent
  }]">
    <div v-if="$slots.header || title" class="app-card__header">
      <h3 v-if="title" class="app-card__title">{{ title }}</h3>
      <slot name="header"></slot>
      
      <div v-if="$slots.action" class="app-card__actions">
        <slot name="action"></slot>
      </div>
    </div>
    
    <div class="app-card__body">
      <slot></slot>
    </div>
    
    <div v-if="$slots.footer" class="app-card__footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { computed } from 'vue'

// Define props interface
interface Props {
  title?: string
  hover?: boolean
  flat?: boolean
  border?: boolean
  accent?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | ''
}

// Define props with defaults
const props = withDefaults(defineProps<Props>(), {
  title: '',
  hover: true,
  flat: false,
  border: false,
  accent: ''
})
</script>

<style scoped>
/* Use design system app-card classes from construction-app.css */
.app-card {
  background-color: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: all var(--transition-base) cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--neutral-200);
  height: fit-content;
  display: flex;
  flex-direction: column;
  position: relative;
}

.app-card--hover:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: var(--neutral-300);
}

.app-card--flat {
  box-shadow: none;
  border: 1px solid var(--neutral-200);
}

.app-card--border {
  box-shadow: var(--shadow-xs);
  border: 1px solid var(--neutral-300);
}

/* Accent colors using design system */
.app-card--accent-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary) 0%, var(--primary-light) 100%);
}

.app-card--accent-success::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--success) 0%, var(--success-light) 100%);
}

.app-card--accent-warning::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--warning) 0%, var(--warning-light) 100%);
}

.app-card--accent-danger::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--error) 0%, var(--error-light) 100%);
}

.app-card--accent-info::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--info) 0%, var(--info-light) 100%);
}

.app-card__header {
  padding: var(--space-5) var(--space-6);
  border-bottom: 1px solid var(--neutral-200);
  background: linear-gradient(135deg, var(--neutral-50) 0%, white 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.app-card__title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-800);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.app-card__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.app-card__body {
  padding: var(--space-6);
  flex: 1;
}

.app-card__footer {
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--neutral-200);
  background-color: var(--neutral-50);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
